import React from "react";
import Skeleton from "../common/skeleton";
import SectionError from "./section_error";

const formatKrw = (n) => "₩" + Math.round(Number(n) || 0).toLocaleString();
const formatKrwSigned = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+₩" : "-₩") + Math.abs(Math.round(v)).toLocaleString();
};
const formatUsd = (n) =>
    "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatPct = (n) => {
    const v = Number(n) || 0;
    return (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
};

function StatCell({ label, value, valueClass, wide }) {
    return (
        <div
            className={`rounded-xl border border-surface-border bg-surface-card-alt px-3 py-2.5 ${
                wide ? "col-span-2 sm:col-span-1" : ""
            }`}
        >
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`mt-1 font-num text-lg font-semibold ${valueClass || "text-content"}`}>
                {value}
            </div>
        </div>
    );
}

/**
 * StockSummaryHero — ① Hero 요약.
 * 총 평가액(KRW 크게 + USD 병기) · 누적 투입원금 · 평가손익 · 수익률 · 환율 칩.
 */
function StockSummaryHero({ summary, loading, error, onRetry }) {
    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <Skeleton variant="line" width="40%" height={14} />
                <Skeleton variant="line" width="60%" height={36} className="mt-3" />
                <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
                    <Skeleton variant="rect" height={64} />
                    <Skeleton variant="rect" height={64} />
                    <Skeleton variant="rect" height={64} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <SectionError message="요약 정보를 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const s = summary || {};
    const pnlPositive = Number(s.pnl_krw) >= 0;
    // 원화 예수금 (DAS-170) — 총 평가액에 이미 합산되어 내려온다. 0이면 표시 생략(구 백엔드 호환).
    const cashKrw = Number(s.cash_krw) || 0;

    // 계좌/증권사별 연동 상태 배지 — api 연동은 gain 점, 수기 입력은 muted 점으로 구분한다.
    // accounts[]가 있으면 계좌 단위(별칭 표시)로 우선하고, 없으면 brokers[] → source 단일 필드 순으로
    // 폴백한다 (DAS-171 P1 E-4, DAS-169).
    const brokerBadges = Array.isArray(s.accounts) && s.accounts.length > 0
        ? s.accounts.map((a) => ({ key: a.account_id, label: a.label, linked: a.linked }))
        : Array.isArray(s.brokers) && s.brokers.length > 0
            ? s.brokers.map((b) => ({ key: b.broker, label: b.label, linked: b.linked }))
            : (s.source === "kiwoom"
                ? [{ key: "kiwoom", label: "키움증권", linked: true }]
                : []);

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="text-xs text-content-muted">총 평가액</div>
                    <div className="mt-1 font-num text-2xl font-semibold text-content sm:text-3xl">
                        {formatKrw(s.total_value_krw)}
                    </div>
                    <div className="mt-0.5 font-num text-xs text-content-muted">
                        {formatUsd(s.total_value_usd)}
                        {cashKrw > 0 && <span className="ml-1.5">· 예수금 포함</span>}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {/* 증권사별 연동 상태 배지 (DAS-169 — 브로커 다중화) */}
                    {brokerBadges.map((b) => (
                        <span
                            key={b.key}
                            className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2.5 py-1 text-xs font-medium text-content-secondary"
                            title={b.linked ? `${b.label} 실계좌 잔고 기준 (5분 캐시)` : `${b.label} 수기 입력 기준`}
                        >
                            <span className={`h-1.5 w-1.5 rounded-full ${b.linked ? "bg-gain" : "bg-content-muted"}`} aria-hidden="true" />
                            {b.label}{b.linked ? " 연동" : " 수기"}
                        </span>
                    ))}
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-surface-border bg-surface-card-alt px-3 py-1.5 text-xs text-content-secondary">
                        <span>USD/KRW</span>
                        <span className="font-num text-content">
                            {Number(s.usd_krw || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                    </span>
                </div>
            </div>

            {/* 예수금이 있으면 4칸(모바일 2×2), 없으면 3칸 (DAS-170).
                ⚠️ 모바일에서 3열을 유지하면 셀 폭이 82px인데 "₩10,459,880"은 18px 고정폭으로 119px라
                숫자가 두 줄로 접힌다 (DAS-178 실측). 가장 긴 값인 누적 투입원금에 전폭을 주고 나머지를
                2열로 두면 읽는 순서를 지킨 채 셋 다 한 줄에 들어간다. sm: 이상은 종전 그대로. */}
            <div className={`mt-4 grid gap-2 sm:gap-3 ${cashKrw > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3"}`}>
                <StatCell label="누적 투입원금" value={formatKrw(s.principal_krw)} wide={cashKrw === 0} />
                <StatCell
                    label="평가손익"
                    value={formatKrwSigned(s.pnl_krw)}
                    valueClass={pnlPositive ? "text-gain" : "text-loss"}
                />
                <StatCell
                    label="수익률"
                    value={formatPct(s.pnl_pct)}
                    valueClass={pnlPositive ? "text-gain" : "text-loss"}
                />
                {cashKrw > 0 && <StatCell label="원화 예수금" value={formatKrw(cashKrw)} />}
            </div>
        </div>
    );
}

export default StockSummaryHero;
